/* eslint no-undef: "off"*/
import { zodResolver } from '@hookform/resolvers/zod';
import { message } from 'antd';
import { useUpdateProfile } from 'lib/hooks/user/user.mutations';
import { useFetchMe } from 'lib/hooks/user/user.queries';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import InputMask from 'react-text-mask';
import PlacesAutocomplete from '../../../components/PlaceSearchBox';
import './styles.scss';

import { defaultApiErrorSchema, parseApiErrorMessage } from 'lib/api/utils';
import { companyNameSchema } from 'schemas/common.schemas';
import { UserRole } from 'types/shared';
import { z } from 'zod';

const phoneNumberMask = [
  '(',
  /[1-9]/,
  /\d/,
  /\d/,
  ')',
  ' ',
  /\d/,
  /\d/,
  /\d/,
  '-',
  /\d/,
  /\d/,
  /\d/,
  /\d/,
];

/**
 * This regular expression is designed to validate US street addresses.
 *
 * * `(\d{1,5}\s)?` - Optional 1-5 digits followed by a space
 * * `([A-Za-z\s\d.#\-',]*)` - A combination of letters, numbers, spaces, periods, hash symbols, hyphens, apostrophes, and commas
 *
 * Examples of valid street addresses:
 * - 123 Main Street
 * - 42nd Street
 * - Broadway
 * - 5th Ave.
 * - 10 Downing St.
 * - 221B Baker Street
 * - O'Connor Street
 * - Martin Luther King Jr. Blvd.
 */

const streetRegex = /^(\d{1,5}\s)?([A-Za-z\s\d.#\-',]*)$/;

const updateProfileFormSchema = z.object({
  companyname: companyNameSchema,
  firstname: z
    .string()
    .trim()
    .min(1, 'First name is required')
    .max(25, 'First name cannot exceed 25 characters'),
  lastname: z
    .string()
    .trim()
    .min(1, 'Last name is required')
    .max(25, 'Last name cannot exceed 25 characters'),
  email: z
    .string()
    .trim()
    .min(1, 'Email is required')
    .email('Invalid email format')
    .max(50, 'Email cannot exceed 50 characters'),
  phone: z
    .string({ message: 'Phone number must be a string' })
    .trim()
    .min(1, { message: 'Phone number is required' })
    .refine((val) => /^\d{10,15}$/.test(val.replace(/\D/g, '')), {
      message: 'Phone number must be at least 10 digits long',
    }),
  addressline1: z
    .string()
    .min(1, 'Address line 1 is required')
    .regex(streetRegex, 'Please enter a valid street address'),
  addressline2: z
    .string()
    .regex(streetRegex, 'Please enter a valid street address')
    .optional(),
  city: z
    .string()
    .min(1, 'City is required')
    .regex(
      /^[A-Za-z\s\-'.]+$/,
      'City name should contain english alphabets or hyphens only'
    ),
  state: z
    .string()
    .min(1, 'State is required')
    .regex(
      /^[A-Za-z\s\-'.]+$/,
      'State should contain english alphabets or hyphens only'
    ),
  zipcode: z
    .string()
    .min(1, 'Zipcode is required')
    .length(5, 'Zipcode must be 5 digits')
    .regex(/^\d+$/, 'Zipcode must contain digits only'),
  countryCode: z.string().min(1, 'Country code is required'),
});

type UpdateProfileFormSchema = z.infer<typeof updateProfileFormSchema>;

export default function MyProfileComponent() {
  const {
    register,
    formState: { errors, isSubmitting },
    ...form
  } = useForm<UpdateProfileFormSchema>({
    defaultValues: {
      companyname: '',
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      addressline1: '',
      addressline2: '',
      city: '',
      state: '',
      zipcode: '',
      countryCode: '1',
    },
    resolver: zodResolver(updateProfileFormSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    // Set the document title when the component mounts
    document.title = 'Profile | UltraIoT';
  }, []);

  // Fetch user profile data
  const { data: userProfile } = useFetchMe();
  const { mutateAsync: updateProfile } = useUpdateProfile();

  const formatPhoneNumber = (phoneNumberString: string) => {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    let match = null;
    if (phoneNumberString.length === 8) {
      match = cleaned.match(/^(\d{3})(\d{3})(\d{2})$/);
    } else if (phoneNumberString.length === 9) {
      match = cleaned.match(/^(\d{3})(\d{3})(\d{3})$/);
    } else {
      match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    }
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return null;
  };

  useEffect(() => {
    if (userProfile?.user) {
      form.reset({
        companyname: userProfile.user.companyname,
        firstname: userProfile.user.firstname,
        lastname: userProfile.user.lastname,
        email: userProfile.user.email,
        phone: formatPhoneNumber(userProfile.user.phone ?? '') ?? '',
        addressline1: userProfile.user.addressline1 ?? '',
        addressline2: userProfile.user.addressline2 ?? '',
        city: userProfile.user.city ?? '',
        state: userProfile.user.state ?? '',
        zipcode: userProfile.user.zipcode ?? '',
        countryCode: userProfile.user.countryCode ?? '1',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile]);

  const success = (msg: string) => {
    message.success({ key: 'updatable', content: msg });
  };
  const handleUpdateProfile = async (data: UpdateProfileFormSchema) => {
    if (!userProfile?.user?.id) return;
    const updatedData = {
      id: userProfile.user.id,
      companyname: data.companyname.trim(),
      firstname: data.firstname.trim(),
      lastname: data.lastname.trim(),
      email: data.email.trim(),
      countryCode: data.countryCode,
      phone: data.phone.replace(/\D/g, ''), // Remove non-digit characters
      addressline1: data.addressline1.trim(),
      addressline2: data?.addressline2 ?? '',
      city: data.city.trim(),
      state: data.state.trim(),
      zipcode: data.zipcode.trim(),
      role: userProfile.user.role,
    };
    try {
      const resp = await updateProfile(updatedData);
      success(resp?.data?.message || 'Your changes have been saved!');
    } catch (error) {
      const parsedError = defaultApiErrorSchema
        .extend({
          data: z.object({
            duplicateFields: z.array(
              z.object({
                field: z.enum(['companyname', 'email', 'phone']),
                message: z.string(),
              })
            ),
          }),
        })
        .safeParse(error);
      if (
        parsedError.success &&
        parsedError.data.data.duplicateFields.length > 0
      ) {
        const duplicateFields = parsedError.data.data.duplicateFields;
        duplicateFields.forEach((field) => {
          form.setError(field.field, {
            message: field.message,
          });
        });
      } else {
        const errorMessage = parseApiErrorMessage(error);
        form.setError('root', {
          message: errorMessage ?? 'Something went wrong',
        });
      }
    }
  };

  return (
    <div className="card-container">
      <div className="card-wrapper">
        <div className="card mb-0">
          <form onSubmit={form.handleSubmit(handleUpdateProfile)} noValidate>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group material-textfield">
                    <input
                      type="text"
                      id="firstname"
                      className="form-control form-control-lg material-textfield-input"
                      {...register('firstname')}
                      required
                    />
                    <label
                      className="material-textfield-label"
                      htmlFor="firstname"
                    >
                      First Name <span className="text-danger">*</span>
                    </label>
                    <span className="invalid-feedback d-block">
                      {errors.firstname?.message}
                    </span>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group material-textfield">
                    <input
                      type="text"
                      id="lastname"
                      className="form-control form-control-lg material-textfield-input"
                      {...register('lastname')}
                      required
                    />
                    <label
                      className="material-textfield-label"
                      htmlFor="lastname"
                    >
                      Last Name <span className="text-danger">*</span>
                    </label>
                    <span className="invalid-feedback d-block">
                      {errors.lastname?.message}
                    </span>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-3">
                  <Controller
                    name="countryCode"
                    control={form.control}
                    render={({ field }) => (
                      <PhoneInput
                        {...field}
                        country={'us'}
                        inputProps={{
                          id: 'countryCode',
                          required: true,
                          className:
                            'form-control form-control-lg material-textfield-input',
                        }}
                      />
                    )}
                  />
                </div>
                <div className="col-md-9">
                  <div className="form-group material-textfield">
                    <Controller
                      name="phone"
                      control={form.control}
                      render={({ field }) => (
                        <InputMask
                          {...field}
                          ref={null}
                          guide={false}
                          type="text"
                          id="phone"
                          keepCharPositions={false}
                          mask={phoneNumberMask}
                          className="form-control form-control-lg material-textfield-input"
                          required
                        />
                      )}
                    />
                    <label className="material-textfield-label" htmlFor="phone">
                      Phone Number <span className="text-danger">*</span>
                    </label>
                    <span className="invalid-feedback d-block">
                      {errors.phone?.message}
                    </span>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-12">
                  <div className="form-group material-textfield">
                    <input
                      type="text"
                      id="email"
                      className="form-control form-control-lg material-textfield-input"
                      {...register('email')}
                      required
                    />
                    <label className="material-textfield-label" htmlFor="email">
                      Email Address <span className="text-danger">*</span>{' '}
                    </label>
                    <span className="invalid-feedback d-block">
                      {errors.email?.message}
                    </span>
                  </div>
                </div>
              </div>
              <>
                <div className="form-group material-textfield">
                  <input
                    type="text"
                    id="companyname"
                    className="form-control form-control-lg material-textfield-input"
                    {...register('companyname')}
                    disabled={userProfile?.user.role === UserRole.SUB_USER}
                    required
                  />
                  <label
                    className="material-textfield-label"
                    htmlFor="companyname"
                  >
                    Company Name <span className="text-danger">*</span>
                  </label>
                  <span className="invalid-feedback d-block">
                    {errors.companyname?.message}
                  </span>
                </div>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group material-textfield">
                      <PlacesAutocomplete
                        type="text"
                        className="form-control form-control-lg material-textfield-input"
                        isRequired={true}
                        address={form.watch('addressline1')}
                        onChange={(value: string) =>
                          form.setValue('addressline1', value, {
                            shouldValidate: true,
                          })
                        }
                        handleAddressChange={({
                          address,
                          city,
                          state,
                          zip,
                        }: {
                          address: string;
                          city: string;
                          state: string;
                          zip: string;
                        }) => {
                          form.setValue('addressline1', address, {
                            shouldValidate: true,
                          });
                          form.setValue('city', city, { shouldValidate: true });
                          form.setValue('state', state, {
                            shouldValidate: true,
                          });
                          form.setValue('zipcode', zip, {
                            shouldValidate: true,
                          });
                        }}
                        setValue={(value: string) =>
                          form.setValue('addressline1', value, {
                            shouldValidate: true,
                          })
                        }
                      />
                      <span className="invalid-feedback d-block">
                        {errors.addressline1?.message}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-12">
                    <div className="form-group material-textfield">
                      <input
                        type="text"
                        id="addressline2"
                        className="form-control form-control-lg material-textfield-input"
                        {...register('addressline2')}
                        required
                      />
                      <label
                        className="material-textfield-label"
                        htmlFor="addressline2"
                      >
                        Address Line 2
                      </label>
                      {errors.addressline2 && (
                        <span className="invalid-feedback d-block">
                          {errors.addressline2.message}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-4">
                    <div className="form-group material-textfield">
                      <input
                        type="text"
                        id="city"
                        className="form-control form-control-lg material-textfield-input"
                        {...register('city')}
                        required
                      />
                      <label
                        className="material-textfield-label"
                        htmlFor="city"
                      >
                        City <span className="text-danger">*</span>
                      </label>
                      <span className="invalid-feedback d-block">
                        {errors.city?.message}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group material-textfield">
                      <input
                        type="text"
                        id="state"
                        className="form-control form-control-lg material-textfield-input"
                        {...register('state')}
                        required
                      />
                      <label
                        className="material-textfield-label"
                        htmlFor="state"
                      >
                        State <span className="text-danger">*</span>
                      </label>
                      <span className="invalid-feedback d-block">
                        {errors.state?.message}
                      </span>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group material-textfield">
                      <input
                        type="text"
                        id="zipcode"
                        className="form-control form-control-lg material-textfield-input"
                        {...register('zipcode')}
                        required
                      />
                      <label
                        className="material-textfield-label"
                        htmlFor="zipcode"
                      >
                        Zip <span className="text-danger">*</span>
                      </label>
                      <span className="invalid-feedback d-block">
                        {errors.zipcode?.message}
                      </span>
                    </div>
                  </div>
                </div>
              </>

              <div className="invalid-feedback d-block text-center mb-3">
                {errors.root?.message}
              </div>
              <button
                type="submit"
                className="btn btn-dark btn-lg w-100"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Please wait...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
